"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";

import { CcShowcaseHeightShell } from "@/components/services/shared/CcShowcaseHeightShell";
import { CcButton } from "@/components/ui/CcButton";
import { CcFaqPillTabs } from "@/components/ui/CcFaqPillTabs";
import { CcPillTabList } from "@/components/ui/CcPillTabList";
import { CcDigitRollText } from "@/components/ui/CcDigitRoll";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { CcTrafficSliderField } from "./CcTrafficSliderField";
import {
  SeoContactPhotoView,
  SeoFoundPhotoView,
  SeoVisitPhotoView,
} from "./CcSeoValuePhotoViews";
import { devSectionAttr } from "@/lib/dev/section-attr";

import {
  REVENUE_PROJECTION_YEARS,
  TRAFFIC_CALCULATOR_SLIDER_RANGES,
  formatWholeNumber,
  projectRevenue,
  type RevenueField,
  type SalesModel,
  calculatorCurrency,
  moneyRange,
} from "./seo-revenue-projection";

const REVENUE_KPI_COUNT = 4;
const CHART_ROLL_STAGGER_MS = 120;

/** v1 traffic results use in-view digit roll; v2 shows formatted values immediately. */
function TrafficCalculatorRollText({
  text,
  digitRoll,
  enabled = true,
  threshold = 0.28,
}: {
  text: string;
  digitRoll: boolean;
  enabled?: boolean;
  threshold?: number;
}) {
  if (!digitRoll) {
    return <>{text}</>;
  }
  return (
    <CcDigitRollText text={text} enabled={enabled} threshold={threshold} />
  );
}
import type { SeoValueDemoCopy } from "./seo-value-demo-copy";
import type { SeoTrafficCalculatorVersion } from "./seo-traffic-calculator-versions";
import { CcTrafficSelect } from "./CcTrafficSelect";
import { CcRoiResultImpactPanel } from "@/components/services/shared/calculator/CcRoiResultImpactPanel";
import { CcSliderResultsCalculatorLayout } from "@/components/services/shared/calculator/CcSliderResultsCalculatorLayout";

/** Intäktskalkyl / revenue traffic calculator layout version. */
export type TrafficCalculatorVersion = SeoTrafficCalculatorVersion;

type CcSeoValueProps = {
  copy: SeoValueDemoCopy;
  /** Rendered after the tab demo and before Intäktskalkyl (e.g. customer results). */
  children?: ReactNode;
};

type TabKey = "found" | "visit" | "contact";
const TABS: TabKey[] = ["found", "visit", "contact"];

/**
 * "Värdet av SEO": three tabs following one made-up company from Google to
 * its site to its inbox, each a real photo of a person with realistic cards
 * (real logos) laid over it. Optional slot content (e.g. customer
 * results) sits before the revenue calculator. All demo views share one grid
 * cell so the height is stable.
 */
export function CcSeoValue({ copy, children }: CcSeoValueProps) {
  const baseId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const [active, setActive] = useState<TabKey>("found");
  const demoRef = useRef<HTMLDivElement | null>(null);

  const select = useCallback((key: string) => {
    setActive(key as TabKey);
  }, []);

  // Each view's single intro effect waits until the demo is on screen.
  useEffect(() => {
    const demo = demoRef.current;
    if (!demo) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        demo.dataset.inview = "true";
      },
      { threshold: 0.3 },
    );
    observer.observe(demo);
    return () => observer.disconnect();
  }, []);

  const tabId = (key: TabKey) => `${baseId}-tab-${key}`;
  const panelId = (key: TabKey) => `${baseId}-panel-${key}`;

  const tabItems = useMemo(
    () =>
      TABS.map((key) => ({
        id: key,
        label: copy.tabs[key],
        tabId: `${baseId}-tab-${key}`,
        controlsId: `${baseId}-panel-${key}`,
      })),
    [baseId, copy.tabs],
  );

  return (
    <section
      className="cc-section cc-seo-value"
      aria-labelledby="cc-seo-value-title"
      data-cc-nav="light"
      {...devSectionAttr("seo-value")}
    >
      <div className="cc-container">
        <header className="cc-seo-value__header">
          <CcSanjayaEyebrow theme="on-subtle">
            {copy.eyebrow}
          </CcSanjayaEyebrow>
          <h2 id="cc-seo-value-title" className="cc-seo-heading">
            {copy.title}
          </h2>
          <p className="cc-seo-lead cc-seo-value__lead">{copy.lead}</p>
        </header>

        <div ref={demoRef} className="cc-demo">
          <div className="cc-demo__top">
            <CcPillTabList
              items={tabItems}
              activeId={active}
              onSelect={select}
              ariaLabel={copy.tablistLabel}
              scrollClassName="cc-demo__tabs-scroll"
            />
            <p className="cc-demo__disclaimer">{copy.disclaimer}</p>
          </div>

          <CcShowcaseHeightShell
            shellClassName="cc-demo__stage-shell"
            innerClassName="cc-demo__stage"
            remeasureKey={active}
          >
            {TABS.map((key) => (
              <div
                key={key}
                id={panelId(key)}
                role="tabpanel"
                aria-labelledby={tabId(key)}
                className={`cc-demo__view cc-demo__view--${key}`}
                data-active={active === key}
                aria-hidden={active !== key}
                inert={active !== key}
              >
                <p className="cc-demo__summary">{copy[key].summary}</p>
                {key === "found" ? <SeoFoundPhotoView copy={copy.found} /> : null}
                {key === "visit" ? <SeoVisitPhotoView copy={copy.visit} /> : null}
                {key === "contact" ? (
                  <SeoContactPhotoView copy={copy.contact} />
                ) : null}
              </div>
            ))}
          </CcShowcaseHeightShell>
        </div>

        <p className="cc-seo-value__footnote">{copy.footnote}</p>

        {children}

        <div className="cc-seo-value__traffic cc-seo-value__traffic--v1">
          <TrafficCalculator copy={copy} version={1} />
        </div>
        <div className="cc-seo-value__traffic cc-seo-value__traffic--v2">
          <TrafficCalculator copy={copy} version={2} />
        </div>
      </div>
    </section>
  );
}

/** Prefilled example assumptions (labelled as such in the UI). */
const DEFAULTS = {
  visits: "1 000",
  value: "30 000",
  model: "lead" as SalesModel,
  growth: "30",
  conversion: { online: "2", lead: "3" } as Record<SalesModel, string>,
  close: "25",
};

/**
 * Revenue calculator: visits today, how they sell and the value of a deal →
 * possible extra revenue in years 1–5. Assumptions sit behind "Justera
 * antaganden" but stay summarised (v1); v2 uses the toggle label only.
 * Formulas behind "Så räknar vi".
 */
function TrafficCalculator({
  copy,
  version,
}: {
  copy: SeoValueDemoCopy;
  version: TrafficCalculatorVersion;
}) {
  const ids = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const traffic = copy.traffic;
  const money = new Intl.NumberFormat(copy.locale, {
    style: "currency",
    currency: calculatorCurrency(copy.locale),
    maximumFractionDigits: 0,
  });
  const percent = (raw: string) =>
    copy.locale.startsWith("en") ? raw.trim().replace(",", ".") : raw.trim().replace(".", ",");

  const [visits, setVisits] = useState(DEFAULTS.visits);
  const [value, setValue] = useState(() =>
    ["GBP", "DKK", "EUR"].includes(calculatorCurrency(copy.locale))
      ? formatWholeNumber(moneyRange(TRAFFIC_CALCULATOR_SLIDER_RANGES.value, copy.locale).fallback, copy.locale)
      : DEFAULTS.value,
  );
  const [model, setModel] = useState<SalesModel>(DEFAULTS.model);
  const [growth, setGrowth] = useState(DEFAULTS.growth);
  const [conversion, setConversion] = useState(DEFAULTS.conversion[DEFAULTS.model]);
  const [conversionTouched, setConversionTouched] = useState(false);
  const [close, setClose] = useState(DEFAULTS.close);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [chartInView, setChartInView] = useState(false);
  const [chartRollEnabled, setChartRollEnabled] = useState(() =>
    Array(REVENUE_PROJECTION_YEARS).fill(false),
  );
  const digitRollResults = version !== 2;
  const [kpiRollEnabled, setKpiRollEnabled] = useState(() =>
    Array(REVENUE_KPI_COUNT).fill(false),
  );

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        chart.dataset.inview = "true";
        setChartInView(true);
      },
      { threshold: 0.28 },
    );
    observer.observe(chart);
    return () => observer.disconnect();
  }, []);

  // Stagger digit roll on v1 only: projection bars then KPI grid.
  useEffect(() => {
    if (!digitRollResults || !chartInView) return;
    const timers: number[] = [];
    const kpiOffset = REVENUE_PROJECTION_YEARS;

    for (let index = 0; index < REVENUE_KPI_COUNT; index += 1) {
      timers.push(
        window.setTimeout(() => {
          setKpiRollEnabled((prev) => {
            const next = [...prev];
            next[index] = true;
            return next;
          });
        }, (kpiOffset + index) * CHART_ROLL_STAGGER_MS),
      );
    }
    for (let index = 0; index < REVENUE_PROJECTION_YEARS; index += 1) {
      timers.push(
        window.setTimeout(() => {
          setChartRollEnabled((prev) => {
            const next = [...prev];
            next[index] = true;
            return next;
          });
        }, index * CHART_ROLL_STAGGER_MS),
      );
    }
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [chartInView, digitRollResults]);

  const result = projectRevenue({
    visitsRaw: visits,
    valueRaw: value,
    model,
    growthRaw: growth,
    conversionRaw: conversion,
    closeRaw: close,
  });
  const problem = (field: RevenueField) =>
    result.kind !== "incomplete"
      ? null
      : result.invalid.includes(field)
        ? field === "visits" || field === "value"
          ? traffic.wholeNumberInvalid
          : traffic.percentInvalid
        : result.empty.includes(field)
          ? traffic.missing[field]
          : null;
  const assumptionProblem =
    problem("growth") !== null ||
    problem("conversion") !== null ||
    (model === "lead" && problem("close") !== null);
  const showAssumptions = assumptionsOpen || assumptionProblem;

  const onModel = (next: SalesModel) => {
    setModel(next);
    // Keep the example rate in step with the sales model until it's edited.
    if (!conversionTouched) setConversion(DEFAULTS.conversion[next]);
  };

  const summaryLines = [
    traffic.summaryGrowth.replace("{n}", percent(growth) || "–"),
    (model === "online"
      ? traffic.summaryConversionOnline
      : traffic.summaryConversionLead
    ).replace("{n}", percent(conversion) || "–"),
    ...(model === "lead"
      ? [traffic.summaryClose.replace("{n}", percent(close) || "–")]
      : []),
  ];

  const max =
    result.kind === "value" ? Math.max(1, ...result.years) : 1;
  const showIntroCta = version !== 2;
  const useSliders = version === 2;

  const numberField = (
    field: RevenueField,
    label: string,
    raw: string,
    onChange: (next: string) => void,
    options: { unit?: string; help?: string; percent?: boolean } = {},
  ) => {
    const error = problem(field);
    const describedBy = [
      error ? `${ids}-${field}-error` : null,
      options.help ? `${ids}-${field}-help` : null,
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <div className={`cc-traffic__field cc-traffic__field--${field}`}>
        <label htmlFor={`${ids}-${field}`} className="cc-traffic__label">
          {label}
        </label>
        <div className="cc-traffic__control">
          <span
            className={`cc-traffic__affix${options.percent ? " is-percent" : ""}`}
          >
            <input
              id={`${ids}-${field}`}
              className="cc-traffic__input"
              inputMode={options.percent ? "decimal" : "numeric"}
              autoComplete="off"
              value={raw}
              onChange={(event) => onChange(event.target.value)}
              aria-invalid={error !== null}
              aria-describedby={describedBy || undefined}
            />
            {options.unit ? (
              <span className="cc-traffic__unit" aria-hidden="true">
                {options.unit}
              </span>
            ) : null}
          </span>
        </div>
        {error ? (
          <p id={`${ids}-${field}-error`} className="cc-traffic__help is-error">
            {error}
          </p>
        ) : options.help ? (
          <p id={`${ids}-${field}-help`} className="cc-traffic__help">
            {options.help}
          </p>
        ) : null}
      </div>
    );
  };

  const sliderField = (
    field: RevenueField,
    label: string,
    raw: string,
    onChange: (next: string) => void,
    options: {
      kind: "whole" | "percent";
      range: (typeof TRAFFIC_CALCULATOR_SLIDER_RANGES)[keyof typeof TRAFFIC_CALCULATOR_SLIDER_RANGES];
      help?: string;
      unit?: string;
    },
  ) => (
    <CcTrafficSliderField
      id={`${ids}-${field}`}
      field={field}
      label={label}
      raw={raw}
      onChange={onChange}
      kind={options.kind}
      range={field === "value" ? moneyRange(options.range, copy.locale) : options.range}
      locale={copy.locale}
      unit={options.unit ?? (options.kind === "percent" ? "%" : undefined)}
      help={options.help}
      error={problem(field)}
    />
  );

  return (
    <div
      className="cc-traffic"
      data-traffic-calculator-version={version}
    >
      <TrafficCalculatorBody
        version={version}
        copy={copy}
        ids={ids}
        traffic={traffic}
        money={money}
        locale={copy.locale}
        visits={visits}
        setVisits={setVisits}
        value={value}
        setValue={setValue}
        model={model}
        onModel={onModel}
        growth={growth}
        setGrowth={setGrowth}
        conversion={conversion}
        setConversion={setConversion}
        setConversionTouched={setConversionTouched}
        close={close}
        setClose={setClose}
        summaryLines={summaryLines}
        result={result}
        max={max}
        numberField={numberField}
        sliderField={sliderField}
        useSliders={useSliders}
        chartRef={chartRef}
        chartRollEnabled={chartRollEnabled}
        kpiRollEnabled={kpiRollEnabled}
        howOpen={howOpen}
        setHowOpen={setHowOpen}
        showAssumptions={showAssumptions}
        setAssumptionsOpen={setAssumptionsOpen}
        assumptionProblem={assumptionProblem}
        showIntroCta={showIntroCta}
      />
    </div>
  );
}

type TrafficCalculatorBodyProps = {
  version: TrafficCalculatorVersion;
  copy: SeoValueDemoCopy;
  ids: string;
  traffic: SeoValueDemoCopy["traffic"];
  money: Intl.NumberFormat;
  locale: string;
  visits: string;
  setVisits: (next: string) => void;
  value: string;
  setValue: (next: string) => void;
  model: SalesModel;
  onModel: (next: SalesModel) => void;
  growth: string;
  setGrowth: (next: string) => void;
  conversion: string;
  setConversion: (next: string) => void;
  setConversionTouched: (next: boolean) => void;
  close: string;
  setClose: (next: string) => void;
  summaryLines: string[];
  result: ReturnType<typeof projectRevenue>;
  max: number;
  numberField: (
    field: RevenueField,
    label: string,
    raw: string,
    onChange: (next: string) => void,
    options?: { unit?: string; help?: string; percent?: boolean },
  ) => ReactNode;
  sliderField: (
    field: RevenueField,
    label: string,
    raw: string,
    onChange: (next: string) => void,
    options: {
      kind: "whole" | "percent";
      range: (typeof TRAFFIC_CALCULATOR_SLIDER_RANGES)[keyof typeof TRAFFIC_CALCULATOR_SLIDER_RANGES];
      help?: string;
      unit?: string;
    },
  ) => ReactNode;
  useSliders: boolean;
  chartRef: React.RefObject<HTMLDivElement | null>;
  chartRollEnabled: boolean[];
  kpiRollEnabled: boolean[];
  howOpen: boolean;
  setHowOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showAssumptions: boolean;
  setAssumptionsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  assumptionProblem: boolean;
  showIntroCta: boolean;
};

function TrafficCalculatorIntro({
  traffic,
  copy,
  showIntroCta,
  layout = "column",
}: {
  traffic: SeoValueDemoCopy["traffic"];
  copy: SeoValueDemoCopy;
  showIntroCta: boolean;
  /** v1: left sticky column; v2: full-width centered header above the grid. */
  layout?: "column" | "header";
}) {
  const content = (
    <>
      <CcSanjayaEyebrow theme="on-subtle">{traffic.eyebrow}</CcSanjayaEyebrow>
      <h3 className="cc-traffic__title">{traffic.title}</h3>
      <p className="cc-traffic__text">{traffic.text}</p>
      {showIntroCta ? (
        <div className="cc-traffic__cta">
          <CcButton
            bookCallPopup
            bookCallVariant="seo"
            label={copy.cta}
            expand
            expandCompact
            className="cc-traffic__button"
          />
        </div>
      ) : null}
    </>
  );

  if (layout === "header") {
    return <header className="cc-traffic__header">{content}</header>;
  }

  return (
    <div className="cc-traffic__intro">
      <div className="cc-traffic__intro-pin">{content}</div>
    </div>
  );
}

function TrafficCalculatorControls({
  version,
  ids,
  traffic,
  visits,
  setVisits,
  value,
  setValue,
  model,
  onModel,
  numberField,
  sliderField,
  useSliders,
}: Pick<
  TrafficCalculatorBodyProps,
  | "version"
  | "ids"
  | "traffic"
  | "visits"
  | "setVisits"
  | "value"
  | "setValue"
  | "model"
  | "onModel"
  | "numberField"
  | "sliderField"
  | "useSliders"
>) {
  const modelOptions = [
    { id: "online" as const, label: traffic.modelOnline },
    { id: "lead" as const, label: traffic.modelLead },
  ];

  return (
    <div className="cc-traffic__controls">
      <div className="cc-traffic__field cc-traffic__field--model">
        {version === 2 ? (
          <span id={`${ids}-model-label`} className="cc-traffic__label">
            {traffic.modelLabel}
          </span>
        ) : (
          <label id={`${ids}-model-label`} htmlFor={`${ids}-model`} className="cc-traffic__label">
            {traffic.modelLabel}
          </label>
        )}
        <div className="cc-traffic__control">
          {version === 2 ? (
            <CcFaqPillTabs
              items={modelOptions.map((option) => ({
                id: option.id,
                label: option.label,
                tabId: `${ids}-model-${option.id}`,
              }))}
              activeId={model}
              onSelect={(next) => onModel(next as SalesModel)}
              ariaLabelledBy={`${ids}-model-label`}
            />
          ) : (
            <CcTrafficSelect
              id={`${ids}-model`}
              aria-labelledby={`${ids}-model-label`}
              value={model}
              onValueChange={(next) => onModel(next as SalesModel)}
              options={modelOptions.map((option) => ({
                value: option.id,
                label: option.label,
              }))}
            />
          )}
        </div>
      </div>

      {useSliders
        ? sliderField("visits", traffic.visitsLabel, visits, setVisits, {
            kind: "whole",
            range: TRAFFIC_CALCULATOR_SLIDER_RANGES.visits,
            help: traffic.visitsHelp,
          })
        : numberField("visits", traffic.visitsLabel, visits, setVisits, {
            help: traffic.visitsHelp,
          })}

      {useSliders
        ? sliderField(
            "value",
            model === "online" ? traffic.valueLabelOnline : traffic.valueLabelLead,
            value,
            setValue,
            {
              kind: "whole",
              range: TRAFFIC_CALCULATOR_SLIDER_RANGES.value,
              unit: traffic.valueUnit,
            },
          )
        : numberField(
            "value",
            model === "online" ? traffic.valueLabelOnline : traffic.valueLabelLead,
            value,
            setValue,
            { unit: traffic.valueUnit },
          )}
    </div>
  );
}

function TrafficCalculatorAssumptions({
  ids,
  traffic,
  model,
  growth,
  setGrowth,
  conversion,
  setConversion,
  setConversionTouched,
  close,
  setClose,
  summaryLines,
  numberField,
  sliderField,
  useSliders,
  showAssumptions,
  setAssumptionsOpen,
  assumptionProblem,
  showAssumptionsSummary = true,
}: Pick<
  TrafficCalculatorBodyProps,
  | "ids"
  | "traffic"
  | "model"
  | "growth"
  | "setGrowth"
  | "conversion"
  | "setConversion"
  | "setConversionTouched"
  | "close"
  | "setClose"
  | "summaryLines"
  | "numberField"
  | "sliderField"
  | "useSliders"
  | "showAssumptions"
  | "setAssumptionsOpen"
  | "assumptionProblem"
> & {
  showAssumptionsSummary?: boolean;
}) {
  return (
    <div className="cc-traffic__assumptions">
      <div className="cc-traffic__assumptions-bar">
        <button
          type="button"
          className="cc-traffic__toggle"
          aria-expanded={showAssumptions}
          aria-controls={`${ids}-assumptions`}
          onClick={() => setAssumptionsOpen((open) => !open)}
          disabled={assumptionProblem}
        >
          {traffic.assumptionsToggle}
          <ChevronDown size={14} strokeWidth={2} aria-hidden />
        </button>
        {showAssumptionsSummary ? (
          <div className="cc-traffic__summary">
            {summaryLines.map((line) => (
              <p key={line} className="cc-traffic__summary-line">
                {line}
              </p>
            ))}
          </div>
        ) : null}
      </div>
      <div
        id={`${ids}-assumptions`}
        className="cc-traffic__assumptions-body"
        data-open={showAssumptions ? "true" : "false"}
        aria-hidden={!showAssumptions}
      >
        <div className="cc-traffic__assumptions-collapse">
          <div
            className="cc-traffic__assumptions-fields"
            inert={showAssumptions ? undefined : true}
          >
            {useSliders
              ? sliderField("growth", traffic.growthLabel, growth, setGrowth, {
                  kind: "percent",
                  range: TRAFFIC_CALCULATOR_SLIDER_RANGES.growth,
                })
              : numberField("growth", traffic.growthLabel, growth, setGrowth, {
                  unit: "%",
                  percent: true,
                })}
            {useSliders
              ? sliderField(
                  "conversion",
                  model === "online"
                    ? traffic.conversionLabelOnline
                    : traffic.conversionLabelLead,
                  conversion,
                  (next) => {
                    setConversion(next);
                    setConversionTouched(true);
                  },
                  {
                    kind: "percent",
                    range: TRAFFIC_CALCULATOR_SLIDER_RANGES.conversion,
                  },
                )
              : numberField(
                  "conversion",
                  model === "online"
                    ? traffic.conversionLabelOnline
                    : traffic.conversionLabelLead,
                  conversion,
                  (next) => {
                    setConversion(next);
                    setConversionTouched(true);
                  },
                  { unit: "%", percent: true },
                )}
            {model === "lead"
              ? useSliders
                ? sliderField("close", traffic.closeLabel, close, setClose, {
                    kind: "percent",
                    range: TRAFFIC_CALCULATOR_SLIDER_RANGES.close,
                  })
                : numberField("close", traffic.closeLabel, close, setClose, {
                    unit: "%",
                    percent: true,
                  })
              : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function TrafficCalculatorHow({
  ids,
  traffic,
  howOpen,
  setHowOpen,
}: Pick<TrafficCalculatorBodyProps, "ids" | "traffic" | "howOpen" | "setHowOpen">) {
  return (
    <div className="cc-traffic__how">
      <button
        type="button"
        className="cc-traffic__toggle"
        aria-expanded={howOpen}
        aria-controls={`${ids}-how`}
        onClick={() => setHowOpen((open) => !open)}
      >
        {traffic.howLink}
        <ChevronDown size={14} strokeWidth={2} aria-hidden />
      </button>
      <div
        id={`${ids}-how`}
        className="cc-traffic__how-body"
        data-open={howOpen ? "true" : "false"}
        aria-hidden={!howOpen}
      >
        <div className="cc-traffic__how-collapse">
          <div className="cc-traffic__how-content" inert={howOpen ? undefined : true}>
            {traffic.how.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TrafficCalculatorProjectionBars({
  traffic,
  money,
  years,
  max,
  chartRollEnabled = [],
  digitRoll,
}: {
  traffic: SeoValueDemoCopy["traffic"];
  money: Intl.NumberFormat;
  years: number[];
  max: number;
  chartRollEnabled?: boolean[];
  digitRoll: boolean;
}) {
  return (
    <ol className="cc-traffic__bars">
      {years.map((amount, index) => (
        <li key={index} className="cc-traffic__bar">
          <span className="cc-traffic__bar-track">
            <span className="cc-traffic__bar-value">
              <TrafficCalculatorRollText
                text={money.format(Math.round(amount))}
                digitRoll={digitRoll}
                enabled={chartRollEnabled[index]}
                threshold={0.28}
              />
            </span>
            <span
              className="cc-traffic__bar-fill"
              aria-hidden="true"
              style={
                {
                  "--cc-bar": Math.max(0.02, amount / max),
                } as CSSProperties
              }
            />
          </span>
          <span className="cc-traffic__bar-label">
            {traffic.yearLabel.replace("{n}", String(index + 1))}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** v2 results column: shared ROI impact panel (SEO traffic projection). */
function TrafficCalculatorResultImpact({
  traffic,
  money,
  locale,
  result,
  chartRef,
  ids,
  howOpen,
  setHowOpen,
}: Pick<
  TrafficCalculatorBodyProps,
  | "traffic"
  | "money"
  | "locale"
  | "result"
  | "chartRef"
  | "ids"
  | "howOpen"
  | "setHowOpen"
>) {
  const impactResult =
    result.kind === "value"
      ? {
          kind: "value" as const,
          data: {
            heroAmount: result.kpis.cumulativeRevenue5y,
            heroSecondaryPercent: result.kpis.extraRevenuePercentYear5,
            callouts: [
              {
                label: traffic.kpiMonthlyYear5,
                text: money.format(Math.round(result.kpis.monthlyExtraRevenueYear5)),
              },
              {
                label: traffic.kpiCustomersYear5,
                text: formatWholeNumber(result.kpis.extraCustomersYear5, locale),
              },
              {
                label: traffic.kpiEquivalentDeals,
                text: formatWholeNumber(result.kpis.equivalentDeals5y, locale),
              },
            ],
            years: [...result.years],
            compareRows:
              result.kpis.baselineAnnualRevenue > 0
                ? [
                    {
                      label: traffic.compareCurrentLabel,
                      value: result.kpis.baselineAnnualRevenue,
                    },
                    {
                      label: traffic.compareProjectedLabel,
                      value: result.kpis.projectedAnnualRevenueYear5,
                    },
                  ]
                : [],
            showZeroNote: result.visits === 0,
          },
        }
      : { kind: "incomplete" as const };

  return (
    <CcRoiResultImpactPanel
      copy={traffic}
      money={money}
      result={impactResult}
      chartRef={chartRef}
      howOpen={howOpen}
      setHowOpen={setHowOpen}
      howId={`${ids}-how`}
    />
  );
}

function TrafficCalculatorResultChart({
  traffic,
  money,
  locale,
  result,
  max,
  chartRef,
  chartRollEnabled,
  kpiRollEnabled,
  includeFootnote,
  includeHow,
  showChartTitle = true,
  ids,
  howOpen,
  setHowOpen,
}: Pick<
  TrafficCalculatorBodyProps,
  | "traffic"
  | "money"
  | "locale"
  | "result"
  | "max"
  | "chartRef"
  | "chartRollEnabled"
  | "kpiRollEnabled"
  | "ids"
  | "howOpen"
  | "setHowOpen"
> & {
  includeFootnote: boolean;
  includeHow: boolean;
  showChartTitle?: boolean;
}) {
  const kpiItems =
    result.kind === "value"
      ? [
          {
            label: traffic.kpiCumulative5y,
            text: money.format(Math.round(result.kpis.cumulativeRevenue5y)),
          },
          {
            label: traffic.kpiCustomersYear5,
            text: formatWholeNumber(result.kpis.extraCustomersYear5, locale),
          },
          {
            label: traffic.kpiMonthlyYear5,
            text: money.format(Math.round(result.kpis.monthlyExtraRevenueYear5)),
          },
          {
            label: traffic.kpiEquivalentDeals,
            text: formatWholeNumber(result.kpis.equivalentDeals5y, locale),
          },
        ]
      : [];
  return (
    <div ref={chartRef} className="cc-traffic__result" aria-live="polite">
      {showChartTitle ? (
        <p className="cc-traffic__chart-title">{traffic.resultTitle}</p>
      ) : null}
      {result.kind === "value" ? (
        <>
          <TrafficCalculatorProjectionBars
            traffic={traffic}
            money={money}
            years={result.years}
            max={max}
            chartRollEnabled={chartRollEnabled}
            digitRoll
          />
          {result.visits === 0 ? (
            <p className="cc-traffic__help">{traffic.zeroNote}</p>
          ) : null}
          <dl className="cc-traffic__kpis">
            {kpiItems.map((item, index) => (
              <div key={item.label} className="cc-traffic__kpi">
                <dt className="cc-traffic__kpi-label">{item.label}</dt>
                <dd className="cc-traffic__kpi-value">
                  <TrafficCalculatorRollText
                    text={item.text}
                    digitRoll
                    enabled={kpiRollEnabled[index]}
                    threshold={0.28}
                  />
                </dd>
              </div>
            ))}
          </dl>
        </>
      ) : (
        <p className="cc-traffic__empty">{traffic.incompleteNote}</p>
      )}
      {includeFootnote ? (
        <p className="cc-traffic__footnote">{traffic.footnote}</p>
      ) : null}
      {includeHow ? (
        <TrafficCalculatorHow
          ids={ids}
          traffic={traffic}
          howOpen={howOpen}
          setHowOpen={setHowOpen}
        />
      ) : null}
    </div>
  );
}

/** v1: intro + single panel; v2: centered header, then controls left / results right. */
function TrafficCalculatorBody(props: TrafficCalculatorBodyProps) {
  const {
    version,
    copy,
    ids,
    traffic,
    money,
    locale,
    visits,
    setVisits,
    value,
    setValue,
    model,
    onModel,
    growth,
    setGrowth,
    conversion,
    setConversion,
    setConversionTouched,
    close,
    setClose,
    summaryLines,
    result,
    max,
    numberField,
    sliderField,
    useSliders,
    chartRef,
    chartRollEnabled,
    kpiRollEnabled,
    howOpen,
    setHowOpen,
    showAssumptions,
    setAssumptionsOpen,
    assumptionProblem,
    showIntroCta,
  } = props;

  const intro = (
    <TrafficCalculatorIntro traffic={traffic} copy={copy} showIntroCta={showIntroCta} />
  );

  const controls = (
    <TrafficCalculatorControls
      version={version}
      ids={ids}
      traffic={traffic}
      visits={visits}
      setVisits={setVisits}
      value={value}
      setValue={setValue}
      model={model}
      onModel={onModel}
      numberField={numberField}
      sliderField={sliderField}
      useSliders={useSliders}
    />
  );

  const assumptions = (
    <TrafficCalculatorAssumptions
      ids={ids}
      traffic={traffic}
      model={model}
      growth={growth}
      setGrowth={setGrowth}
      conversion={conversion}
      setConversion={setConversion}
      setConversionTouched={setConversionTouched}
      close={close}
      setClose={setClose}
      summaryLines={summaryLines}
      numberField={numberField}
      sliderField={sliderField}
      useSliders={useSliders}
      showAssumptions={showAssumptions}
      setAssumptionsOpen={setAssumptionsOpen}
      assumptionProblem={assumptionProblem}
      showAssumptionsSummary={version !== 2}
    />
  );

  if (version === 2) {
    return (
      <CcSliderResultsCalculatorLayout
        eyebrow={traffic.eyebrow}
        title={traffic.title}
        text={traffic.text}
        controls={
          <>
            {controls}
            {assumptions}
          </>
        }
        results={
          <TrafficCalculatorResultImpact
            traffic={traffic}
            money={money}
            locale={locale}
            result={result}
            chartRef={chartRef}
            ids={ids}
            howOpen={howOpen}
            setHowOpen={setHowOpen}
          />
        }
      />
    );
  }

  return (
    <>
      {intro}
      <div className="cc-traffic__panel">
        {controls}
        {assumptions}
        <TrafficCalculatorResultChart
          traffic={traffic}
          money={money}
          locale={locale}
          result={result}
          max={max}
          chartRef={chartRef}
          chartRollEnabled={chartRollEnabled}
          kpiRollEnabled={kpiRollEnabled}
          includeFootnote
          includeHow
          ids={ids}
          howOpen={howOpen}
          setHowOpen={setHowOpen}
        />
      </div>
    </>
  );
}
